import React, { useState } from 'react';
import api from '../../helpers/api';
import { useAngularService } from '../../helpers/get_angular_service';
import redirect from '../../helpers/redirect';
import sources from '../../helpers/sources';
import Button from '../components/button';
import { useExtendState } from '../../helpers/use_extend_state';
import notification from '../../helpers/notification';
import Input from '../components/input';
import Dropdown from '../components/dropdown';
import Textarea from '../components/textarea';
import Checkbox from '../components/checkbox';
import ColorPicker from '../components/color_picker';
import { colorPickerColors } from 'constants/colors';
import ErrorMessage from '../components/error_message';
import useOnMount from 'helpers/use_on_mount';
import useStyles from 'helpers/use_styles';
import useGym from 'helpers/use_gym';
import { spell } from 'helpers/international_spelling';

const EditAppointmentType = () => {
  const $stateParams = useAngularService('$stateParams');
  const loadingAnimation = useAngularService('loadingAnimation');
  const $modal = useAngularService('$modal');
  const GTMService = useAngularService('GTMService');
  const FeatureAvailabilityService = useAngularService('FeatureAvailabilityService');

  const gym = useGym();
  const { styles } = useStyles();

  const [appointmentType, extendAppointmentType, setAppointmentType] = useExtendState<any>(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [errors, setErrors] = useState<any>({});

  const getAppointmentType = () => {
    if ($stateParams.appointmentTypeId) {
      const url = sources.appointmentType($stateParams.gymId, $stateParams.appointmentTypeId);
      api.get(url).then((data) => {
        setAppointmentType(data);
      });
    } else {
      setAppointmentType({
        duration_number: 1,
        duration_unit: 'hours',
        free_appointment: false,
        color: colorPickerColors[0],
      });
    }
  };

  const getEventTypes = () => {
    const url = sources.eventTypes($stateParams.gymId);
    api.get(url, { count: 100 }).then(
      (data) => {
        setEventTypes(data);
      },
      () => {
        notification.error('Unable to load event types');
      },
    );
  };

  useOnMount(() => {
    if (!FeatureAvailabilityService.isAvailable('attendance')) {
      redirect('gym.dashboard');
      return;
    }

    GTMService.trackPage('Appointment Types Edit');
    getAppointmentType();
    getEventTypes();
  });

  const unitPairs = [
    { value: 'minutes', label: 'minute(s)' },
    { value: 'hours', label: 'hour(s)' },
  ];

  const eventColors = colorPickerColors;

  const manageTypes = () => {
    const roomModal = $modal.open({
      template: require('templates/gyms/calendar/events/manage_types.html.slim'),
      controller: 'ManageEventTypesModal',
      resolve: {
        types: () => eventTypes,
      },
    });
    roomModal.result.then(
      () => {
        getEventTypes();
      },
      () => {
        getEventTypes();
      },
    );
  };

  const cancel = () => {
    redirect('gym.appointmentTypes.list');
  };

  const save = () => {
    if (appointmentType.id) {
      const url = sources.appointmentType($stateParams.gymId, appointmentType.id);
      return api.put(url, { appointment_type: appointmentType });
    } else {
      const url = sources.appointmentTypes($stateParams.gymId);
      return api.post(url, { appointment_type: appointmentType });
    }
  };

  const onSave = () => {
    GTMService.trackPage('Appointment Types Edit', 'attempt');
    loadingAnimation.show();

    save().then(
      () => {
        GTMService.trackPage('Appointment Types Edit', 'success');
        redirect('gym.appointmentTypes.list');
      },
      (response) => {
        GTMService.trackPage('Appointment Types Edit', 'error');
        loadingAnimation.hide();
        setErrors(response.data.errors);
        notification.error('Save failed. Please fix the errors below');
      },
    );
  };

  return (
    <section className="content-section">
      <div className="container">
        <header className="worx-combo-box">
          <h2 className="worx-combo-box-headline">
            {$stateParams.appointmentTypeId ? 'Edit' : 'Add'} Appointment Type
          </h2>
        </header>
        {!!appointmentType && (
          <>
            <main>
              <div className="row form-group">
                <label className="control-label col-sm-3 col-lg-2">Name</label>
                <div className="col-sm-9">
                  <Input
                    autoFocus
                    value={appointmentType.name}
                    onChange={(name) => extendAppointmentType({ name })}
                  />
                </div>
              </div>
              <div className="row form-group">
                <label className="control-label col-sm-3 col-lg-2">Event Type</label>
                <div className="col-sm-9">
                  <Dropdown
                    value={appointmentType.event_type_id}
                    options={eventTypes}
                    onSelect={(val) => extendAppointmentType({ event_type_id: val })}
                    placeholder="Select event type"
                  />
                  <span className="inline-action margin-left-8">
                    <Button variant="link" onClick={manageTypes}>
                      Add/manage types
                    </Button>
                  </span>
                </div>
              </div>

              {gym.is_martial_arts && (
                <div className="row form-group">
                  <label className="control-label col-sm-3 col-lg-2">Style</label>
                  <div className="col-sm-9">
                    <Dropdown
                      value={appointmentType.style_id}
                      options={styles}
                      onSelect={(val) => extendAppointmentType({ style_id: val })}
                      placeholder="Select a style"
                    />
                  </div>
                </div>
              )}

              <div className="row form-group">
                <label className="control-label col-sm-3 col-lg-2">Duration</label>
                <div className="col-sm-9">
                  <div className="input-group is-sm">
                    <Input
                      name="duration"
                      type="number"
                      value={appointmentType.duration_number}
                      onChange={(duration_number) => extendAppointmentType({ duration_number })}
                    />
                    <div className="input-group-btn">
                      <Dropdown
                        value={appointmentType.duration_unit}
                        options={unitPairs}
                        onSelect={(val) => extendAppointmentType({ duration_unit: val })}
                      />
                    </div>
                  </div>
                  <ErrorMessage error={errors.duration_number} />
                </div>
              </div>

              <div className="row form-group">
                <label className="control-label col-sm-3 col-lg-2">Max. attendees</label>
                <div className="col-sm-9">
                  <Input
                    type="number"
                    value={appointmentType.participant_limit}
                    onChange={(participant_limit) => extendAppointmentType({ participant_limit })}
                    size="sm"
                  />
                  <div className="helper-text">Leave blank for no limit</div>
                  <ErrorMessage error={errors.participant_limit} />
                </div>
              </div>

              <div className="row form-group">
                <label className="control-label col-sm-3 col-lg-2">Description</label>
                <div className="col-sm-9">
                  <Textarea
                    name="description"
                    value={appointmentType.description}
                    onChange={(description) => extendAppointmentType({ description })}
                  />
                </div>
              </div>
              <div className="row form-group">
                <label className="control-label col-sm-3 col-lg-2">
                  Appointment {spell('Color', gym?.country)}
                </label>
                <div className="col-sm-9">
                  <ColorPicker
                    value={appointmentType.color}
                    onChange={(color) => extendAppointmentType({ color })}
                    colors={eventColors}
                    variant="v2"
                  />
                </div>
              </div>
              <div className="form-group margin-top">
                <Checkbox
                  checked={!appointmentType.free_appointment}
                  onChange={(val) => extendAppointmentType({ free_appointment: !val })}
                >
                  This appointment should deduct one session from the member's membership limit
                </Checkbox>
              </div>
            </main>
            <footer className="worx-combo-box is-filled is-padded">
              <button className="btn btn-success" onClick={onSave}>
                Save
              </button>
              <button className="btn btn-default" onClick={cancel}>
                Cancel
              </button>
            </footer>
          </>
        )}
      </div>
    </section>
  );
};

angular.module('gym').value('EditAppointmentType', EditAppointmentType);
